/** The gateway's API root, e.g. `https://api.kayracare.com/api/v1`. */
export const API_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1').replace(/\/$/, '');
const SESSION_KEY = 'kayra.admin.tokens';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  expiresIn: number;
  tokenType: string;
}

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages?: number;
}

interface Envelope<T> {
  data?: T;
  meta?: PageMeta;
  error?: { code?: string; message?: string; fields?: { field: string; message: string }[] };
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: { field: string; message: string }[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function readTokens(): Tokens | null {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function writeTokens(tokens: Tokens | null): void {
  if (tokens) localStorage.setItem(SESSION_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(SESSION_KEY);
}

function apiError(status: number, payload?: Envelope<unknown>): ApiError {
  return new ApiError(
    status,
    payload?.error?.code || 'request_failed',
    payload?.error?.message || (status === 0 ? 'Could not reach the KAYRA API.' : 'Request failed.'),
    payload?.error?.fields || [],
    payload?.requestId,
  );
}

let refreshing: Promise<Tokens> | null = null;

/**
 * Called with the account as the server sees it after each token refresh. An
 * administrator may have changed an employee's pages since they signed in; the new
 * token carries the new pages, and this lets the console show them.
 */
let principalListener: ((principal: unknown) => void) | null = null;

export function onRefreshedPrincipal(listener: ((principal: unknown) => void) | null): void {
  principalListener = listener;
}

async function refresh(): Promise<Tokens> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const current = readTokens();
    if (!current?.refreshToken) throw new ApiError(401, 'unauthorized', 'Sign in to continue.');
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    });
    const payload = (await response.json()) as Envelope<{ tokens: Tokens; principal?: unknown }>;
    if (!response.ok || !payload.data?.tokens) {
      writeTokens(null);
      throw apiError(response.status, payload);
    }
    writeTokens(payload.data.tokens);
    if (payload.data.principal) principalListener?.(payload.data.principal);
    return payload.data.tokens;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export async function request<T>(
  path: string,
  options: Omit<RequestInit, 'body'> & { body?: unknown; auth?: boolean; retry?: boolean } = {},
): Promise<{ data: T; meta?: PageMeta }> {
  const { body, auth = true, retry = true, headers: initial, ...init } = options;
  const headers = new Headers(initial);
  headers.set('Accept', 'application/json');
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (auth) {
    const tokens = readTokens();
    if (!tokens) throw new ApiError(401, 'unauthorized', 'Sign in to continue.');
    headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path.startsWith('/') ? path : `/${path}`}`, {
      ...init,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw apiError(0);
  }

  if (response.status === 401 && auth && retry) {
    await refresh();
    return request<T>(path, { ...options, retry: false });
  }
  if (response.status === 204) return { data: undefined as T };

  const payload = (await response.json().catch(() => ({}))) as Envelope<T>;
  if (!response.ok || payload.data === undefined) throw apiError(response.status, payload);
  return { data: payload.data, meta: payload.meta };
}

/**
 * Field errors from a 422, keyed by the server's field path (`tiers[0].price`,
 * `items[2].quantity`). The first message per field wins, as on the server.
 */
export function fieldErrorMap(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const out: Record<string, string> = {};
  for (const { field, message } of error.fields) {
    if (field && !out[field]) out[field] = message;
  }
  return out;
}

/** `path?key=value&…` with every value encoded; empty values are left out. */
export function withQuery(
  path: string,
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  if (!query) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${query}`;
}

/**
 * Posts one file as multipart form data, the way the gateway's upload endpoints take
 * it, with the same sign-in and refresh handling as `request`.
 */
export async function uploadFile<T>(path: string, file: File, retry = true): Promise<T> {
  const tokens = readTokens();
  if (!tokens) throw new ApiError(401, 'unauthorized', 'Sign in to continue.');

  const form = new FormData();
  form.append('file', file);

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path.startsWith('/') ? path : `/${path}`}`, {
      method: 'POST',
      // No Content-Type: the browser sets the multipart boundary itself.
      headers: { Accept: 'application/json', Authorization: `Bearer ${tokens.accessToken}` },
      credentials: 'include',
      body: form,
    });
  } catch {
    throw apiError(0);
  }

  if (response.status === 401 && retry) {
    await refresh();
    return uploadFile<T>(path, file, false);
  }
  const payload = (await response.json().catch(() => ({}))) as Envelope<T>;
  if (!response.ok || payload.data === undefined) throw apiError(response.status, payload);
  return payload.data;
}

/**
 * Makes an image path the catalogue stores loadable from the console. Uploaded images
 * are served by the API (`/api/v1/media/…`), so they need its origin; bundled
 * `/assets/` paths are served by the console itself.
 */
export function mediaUrl(path: string): string {
  if (!path.startsWith('/api/')) return path;
  return `${new URL(API_URL).origin}${path}`;
}

export async function all<T>(path: string): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; ; page += 1) {
    const separator = path.includes('?') ? '&' : '?';
    const result = await request<T[]>(`${path}${separator}page=${page}&pageSize=100`);
    rows.push(...result.data);
    const pages = result.meta?.totalPages ?? Math.ceil((result.meta?.total ?? rows.length) / 100);
    if (!result.meta || page >= pages || result.data.length === 0) return rows;
  }
}
